/**
 * Generate Grocery List Page
 * 
 * Main page for generating grocery lists with occasions and stock checking
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
    getOccasions,
    getOccasion,
    createOccasionFromRecipes,
    generateGroceryList,
    generateGroceryListFromOccasion,
    saveGroceryList,
    getUserRecipes,
    getAllStackEntries,
    getGroceryLists,
    getGroceryList,
    getActiveGroceryList
} from '../services/api';
import StockCheckView from '../components/grocery/StockCheckView';
import GroceryListView from '../components/grocery/GroceryListView';
import GroceryListConfirmView from '../components/grocery/GroceryListConfirmView';
import UpcomingMealsReminder from '../components/grocery/UpcomingMealsReminder';
import PremiumPlanningShell from '../components/PremiumPlanningShell';
import OccasionNamePrompt from '../components/OccasionNamePrompt';
import '../styles/modal.css';

const GenerateGroceryListPage = () => {
    const { user, userProfile } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [activeTab, setActiveTab] = useState('lists'); // 'lists' | 'occasions' | 'quick'
    const [occasions, setOccasions] = useState([]);
    const [savedLists, setSavedLists] = useState([]);
    const [dailyLists, setDailyLists] = useState([]); // Daily lists (scheduled_day)
    const [loading, setLoading] = useState(true);
    const [generatingList, setGeneratingList] = useState(false);
    const [error, setError] = useState(null);
    
    // Recipe selection state
    const [selectedRecipes, setSelectedRecipes] = useState(new Set());
    const [recipeServings, setRecipeServings] = useState({}); // { recipeId: servings }
    const [allRecipes, setAllRecipes] = useState([]);
    
    // Stock check state
    const [showStockCheck, setShowStockCheck] = useState(false);
    const [stockCheckData, setStockCheckData] = useState([]);
    const [generatedList, setGeneratedList] = useState(null);
    const [occasionId, setOccasionId] = useState(null);
    const [savedList, setSavedList] = useState(null);
    const [selectedListId, setSelectedListId] = useState(null); // Currently selected list ID
    
    // Name prompt state
    const [showNamePrompt, setShowNamePrompt] = useState(false);
    
    
    // Check for occasionId in URL params or grocery list from navigation state
    useEffect(() => {
        const urlOccasionId = searchParams.get('occasionId');
        if (urlOccasionId) {
            setOccasionId(urlOccasionId);
            handleGenerateFromOccasion(urlOccasionId);
        }
        
        // Check if we have a grocery list from navigation state (from recipe details)
        if (location.state?.groceryList && user) {
            const list = location.state.groceryList;
            // The backend already saves the list, so it should have an id
            if (list.id) {
                setSavedList(list);
                setGeneratedList(list);
                // Reload lists to show the new one (will be called in the user effect)
            } else {
                // Fallback: If somehow it doesn't have an id, save it
                handleSaveNewList(list);
            }
        }
    }, [searchParams, location.state]);

    useEffect(() => {
        if (user) {
            loadOccasions();
            loadRecipes();
            loadDailyLists(); // Load all daily lists
            loadActiveList(); // Load active list as default
            
            // If we have a grocery list from navigation state, use it
            if (location.state?.groceryList?.id) {
                setSavedList(location.state.groceryList);
                setGeneratedList(location.state.groceryList);
                setSelectedListId(location.state.groceryList.id);
            }
        }
    }, [user]);

    // Listen for grocery list and occasion updates from other pages (like Planning Hub)
    useEffect(() => {
        const handleGroceryListUpdate = () => {
            console.log('[GroceryListPage] Grocery list created/updated, refreshing...');
            loadDailyLists();
            loadActiveList();
            loadOccasions();
        };

        const handleOccasionUpdate = () => {
            console.log('[GroceryListPage] Occasion deleted, refreshing...');
            loadOccasions();
            loadDailyLists();
            loadActiveList();
            // Clear saved list if it was deleted
            if (savedList) {
                loadActiveList();
            }
        };

        window.addEventListener('groceryListCreated', handleGroceryListUpdate);
        window.addEventListener('groceryListUpdated', handleGroceryListUpdate);
        window.addEventListener('occasionDeleted', handleOccasionUpdate);
        
        // Also listen for visibility changes to refresh when user returns to tab
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && user) {
                loadDailyLists();
                loadActiveList();
                loadOccasions();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('groceryListCreated', handleGroceryListUpdate);
            window.removeEventListener('groceryListUpdated', handleGroceryListUpdate);
            window.removeEventListener('occasionDeleted', handleOccasionUpdate);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, savedList]);


    const loadOccasions = async () => {
        if (!user) return;
        
        try {
            setLoading(true);
            const token = await user.getIdToken();
            // Load all occasions (both menu_plan and grocery_list types)
            const response = await getOccasions(token, null);
            setOccasions(response.occasions || []);
        } catch (err) {
            console.error('Error loading occasions:', err);
            setError(err.message || 'Failed to load occasions');
        } finally {
            setLoading(false);
        }
    };

    const loadRecipes = async () => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            const userRecipes = await getUserRecipes(user.uid, token);
            const exploreRecipes = await getAllStackEntries();
            setAllRecipes([...userRecipes, ...exploreRecipes]);
        } catch (err) {
            console.error('Error loading recipes:', err);
        }
    };

    const loadDailyLists = async () => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            const response = await getGroceryLists(token);
            const allLists = response.lists || [];
            // Filter for daily lists (scheduled_day source type), scheduled_range lists, and occasion-linked lists
            // This ensures lists generated from Planning Hub and occasions are visible regardless of date
            const daily = allLists.filter(list => 
                list.source_type === 'scheduled_day' || 
                list.source_type === 'scheduled_range' ||
                (list.occasion_id != null) // Include any list linked to an occasion
            );
            // Sort by date (most recent first)
            daily.sort((a, b) => {
                const dateA = a.date_range_start ? new Date(a.date_range_start) : new Date(a.created_at);
                const dateB = b.date_range_start ? new Date(b.date_range_start) : new Date(b.created_at);
                return dateB - dateA;
            });
            setDailyLists(daily);
        } catch (err) {
            console.error('Error loading daily lists:', err);
        }
    };

    const loadActiveList = async () => {
        if (!user) return;
        
        try {
            setLoading(true);
            const token = await user.getIdToken();
            const response = await getActiveGroceryList(token);
            if (response.list) {
                setSavedList(response.list);
                setGeneratedList(response.list);
                setSelectedListId(response.list.id);
            }
        } catch (err) {
            console.error('Error loading active list:', err);
            setError(err.message || 'Failed to load active grocery list');
        } finally {
            setLoading(false);
        }
    };

    const handleViewList = async (listId) => {
        if (!user) return;
        
        try {
            setLoading(true);
            const token = await user.getIdToken();
            const response = await getGroceryList(listId, token);
            const list = response.list || response;
            setSavedList(list);
            setGeneratedList(list);
            setSelectedListId(listId);
        } catch (err) {
            console.error('Error loading list:', err);
            setError(err.message || 'Failed to load grocery list');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveNewList = async (list) => {
        if (!user || !list) return;
        
        try {
            setGeneratingList(true);
            setError(null);
            const token = await user.getIdToken();
            
            const listData = {
                name: list.name || list.occasionName || `Grocery List - ${new Date().toLocaleDateString()}`,
                recipeIds: list.recipeIds || [],
                items: list.items || [],
                occasionId: list.occasionId || null,
                occasion_recipe_servings: list.occasion_recipe_servings || {},
                stock_check_data: list.stock_check_data || []
            };
            
            const saved = await saveGroceryList(listData, token);
            setSavedList(saved.list);
            setGeneratedList(saved.list);
            
            // Reload active list to show the new one
            await loadActiveList();
        } catch (err) {
            console.error('Error saving new list:', err);
            setError(err.message || 'Failed to save grocery list');
        } finally {
            setGeneratingList(false);
        }
    };

    const handleGenerateFromOccasion = async (occId) => {
        if (!user) return;
        
        try {
            setGeneratingList(true);
            setError(null);
            const token = await user.getIdToken();
            
            // First check if occasion has recipes
            const occasion = await getOccasion(occId, token);
            if (!occasion.occasion || !occasion.occasion.recipes || occasion.occasion.recipes.length === 0) {
                setError('This occasion has no recipes. Please add recipes to the occasion first.');
                setGeneratingList(false);
                return;
            }
            
            const result = await generateGroceryListFromOccasion(occId, token);
            
            if (!result || !result.items) {
                setError('Failed to generate grocery list. Please try again.');
                setGeneratingList(false);
                return;
            }
            
            setStockCheckData(result.stockCheckData || []);
            setGeneratedList(result);
            setOccasionId(occId);
            setShowStockCheck(true);
            setActiveTab('quick');
        } catch (err) {
            console.error('Error generating list from occasion:', err);
            const errorMessage = err.error || err.message || 'Failed to generate list from occasion';
            setError(errorMessage);
            setGeneratingList(false);
        } finally {
            setGeneratingList(false);
        }
    };

    const handleRecipeSelection = (recipeId) => {
        const newSelected = new Set(selectedRecipes);
        if (newSelected.has(recipeId)) {
            newSelected.delete(recipeId);
            const newServings = { ...recipeServings };
            delete newServings[recipeId];
            setRecipeServings(newServings);
        } else {
            newSelected.add(recipeId);
            setRecipeServings(prev => ({ ...prev, [recipeId]: 4 })); // Default 4 servings
        }
        setSelectedRecipes(newSelected);
    };

    const handleServingsChange = (recipeId, servings) => {
        setRecipeServings(prev => ({ ...prev, [recipeId]: Math.max(1, servings) }));
    };

    const handleGenerateList = () => {
        if (!user || selectedRecipes.size === 0) {
            setError('Please select at least one recipe');
            return;
        }
        // Show name prompt first
        setShowNamePrompt(true);
    };

    const handleNamePromptContinue = async (occasionName) => {
        setShowNamePrompt(false);
        
        try {
            setGeneratingList(true);
            setError(null);
            const token = await user.getIdToken();
            const recipeIds = Array.from(selectedRecipes);
            
            // Create occasion with user-provided name
            const occasionResponse = await createOccasionFromRecipes(
                recipeIds,
                recipeServings,
                occasionName,
                'grocery_list',
                token
            );
            
            const newOccasionId = occasionResponse.occasion.id;
            setOccasionId(newOccasionId);
            
            // Generate list with stock checking
            const result = await generateGroceryList(
                recipeIds,
                token,
                recipeServings,
                newOccasionId
            );
            
            setStockCheckData(result.stockCheckData || []);
            setGeneratedList(result);
            setShowStockCheck(true);
        } catch (err) {
            console.error('Error generating list:', err);
            setError(err.message || 'Failed to generate grocery list');
        } finally {
            setGeneratingList(false);
        }
    };

    const handleFinalizeList = async () => {
        if (!user || !generatedList) return;
        
        try {
            setGeneratingList(true);
            const token = await user.getIdToken();
            
            const listData = {
                name: generatedList.occasionName || `Grocery List - ${new Date().toLocaleDateString()}`,
                recipeIds: generatedList.recipeIds,
                items: generatedList.items,
                occasionId: occasionId,
                occasion_recipe_servings: recipeServings,
                stock_check_data: stockCheckData
            };
            
            const saved = await saveGroceryList(listData, token);
            setSavedList(saved.list);
            setShowStockCheck(false);
            
            // Dispatch event to notify other pages (like Planning Hub) that a list was created
            if (saved.list && saved.list.id) {
                window.dispatchEvent(new CustomEvent('groceryListCreated', {
                    detail: { listId: saved.list.id, occasionId: occasionId }
                }));
            }
            
            // Reload lists to show the new one
            await loadDailyLists();
        } catch (err) {
            console.error('Error saving list:', err);
            setError(err.message || 'Failed to save grocery list');
        } finally {
            setGeneratingList(false);
        }
    };


    // Handler for when a day is selected from reminder cards
    const handleDaySelected = async (dateKey, listId) => {
        if (listId) {
            try {
                setLoading(true);
                const token = await user.getIdToken();
                const response = await getGroceryList(listId, token);
                // API returns { list: {...} }
                const list = response.list || response;
                setSavedList(list);
                setGeneratedList(list);
                setSelectedListId(listId);
                setError(null);
                // Reload daily lists to refresh the list
                await loadDailyLists();
            } catch (err) {
                console.error('Error loading list:', err);
                setError('Failed to load grocery list');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleListGenerated = async (list) => {
        // Ensure list has proper structure
        if (list && list.id) {
            console.log('handleListGenerated called with list:', list);
            // If list doesn't have items array, fetch it
            if (!list.items || !Array.isArray(list.items) || list.items.length === 0) {
                try {
                    const token = await user.getIdToken();
                    const response = await getGroceryList(list.id, token);
                    const fullList = response.list || response;
                    console.log('Fetched full list in handleListGenerated:', fullList);
                    setSavedList(fullList);
                    setGeneratedList(fullList);
                    setSelectedListId(fullList.id);
                } catch (err) {
                    console.error('Error fetching list in handleListGenerated:', err);
                    // Fall back to using the provided list
                    setSavedList(list);
                    setGeneratedList(list);
                    setSelectedListId(list.id);
                }
            } else {
                setSavedList(list);
                setGeneratedList(list);
                setSelectedListId(list.id);
            }
            setError(null);
            // Reload daily lists to show the new one
            await loadDailyLists();
        } else {
            console.error('Invalid list structure:', list);
            setError('Generated list has invalid structure');
        }
    };

    // Get planning theme from user profile or localStorage
    const planningTheme = userProfile?.session_preferences?.planning_theme 
        || localStorage.getItem('planningTheme') 
        || 'tomato';

    // Always show the active list if we have one
    if (savedList || (user && !loading && !showStockCheck)) {
        return (
            <PremiumPlanningShell theme={planningTheme}>
                <div className="px-8 py-6">
                    <div className="w-full">
                        <div className="bg-neutral-0 p-4 hairline mb-4">
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="font-sans text-3xl font-medium text-[#1b263b]">
                                    Grocery List
                                </h1>
                                <span className="px-2 py-1 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider">PREMIUM</span>
                            </div>
                        </div>
                        
                        {/* Upcoming Meals Reminder Cards */}
                        <UpcomingMealsReminder 
                            onDaySelected={handleDaySelected}
                            onListGenerated={handleListGenerated}
                        />
                        
                        {/* Two-column layout: Daily Lists Sidebar + Selected List */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                            {/* Daily Lists Sidebar */}
                            <div className="lg:col-span-1">
                                <div className="bg-neutral-0 p-4 hairline">
                                    <h2 className="font-sans text-xl font-medium text-[#1b263b] mb-3">
                                        Daily Lists
                                    </h2>
                                    {dailyLists.length === 0 ? (
                                        <p className="font-sans text-sm text-neutral-900 opacity-60">
                                            No daily lists yet. Generate one from the cards above.
                                        </p>
                                    ) : (
                                        <div className="space-y-2">
                                            {dailyLists.map((list) => {
                                                // Format date from date_range_start without timezone conversion
                                                // PostgreSQL DATE columns return as strings in 'YYYY-MM-DD' format
                                                let listDate;
                                                if (list.date_range_start) {
                                                    // Extract date string - PostgreSQL DATE returns as string
                                                    let dateStr;
                                                    if (typeof list.date_range_start === 'string') {
                                                        // Already a date string from PostgreSQL DATE column
                                                        dateStr = list.date_range_start;
                                                    } else if (list.date_range_start instanceof Date) {
                                                        // If it's a Date object, use UTC methods
                                                        const year = list.date_range_start.getUTCFullYear();
                                                        const month = String(list.date_range_start.getUTCMonth() + 1).padStart(2, '0');
                                                        const day = String(list.date_range_start.getUTCDate()).padStart(2, '0');
                                                        dateStr = `${year}-${month}-${day}`;
                                                    } else {
                                                        // Fallback: parse using UTC
                                                        const d = new Date(list.date_range_start);
                                                        dateStr = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
                                                    }
                                                    const [year, month, day] = dateStr.split('-');
                                                    listDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                } else {
                                                    listDate = new Date(list.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                }
                                                const isSelected = selectedListId === list.id;
                                                const itemCount = list.items ? list.items.length : 0;
                                                const checkedCount = list.items ? list.items.filter(item => item.checked).length : 0;
                                                
                                                return (
                                                    <div
                                                        key={list.id}
                                                        className={`p-3 hairline cursor-pointer transition-all ${
                                                            isSelected 
                                                                ? 'bg-[#1b263b] text-neutral-0' 
                                                                : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                                        }`}
                                                        onClick={async () => {
                                                            await handleViewList(list.id);
                                                            setSelectedListId(list.id);
                                                        }}
                                                    >
                                                        <div className="font-sans text-sm font-medium mb-1">
                                                            {listDate}
                                                        </div>
                                                        <div className="font-mono text-xs uppercase tracking-wider opacity-80">
                                                            {checkedCount} / {itemCount} items
                                                        </div>
                                                        {list.is_locked && (
                                                            <div className="font-mono text-xs uppercase tracking-wider opacity-60 mt-1">
                                                                🔒 Locked
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Selected List View */}
                            <div className="lg:col-span-2">
                                {savedList ? (
                                    <GroceryListConfirmView 
                                        list={savedList} 
                                        onUpdate={async () => {
                                            // Reload the current list
                                            if (savedList?.id) {
                                                try {
                                                    const token = await user.getIdToken();
                                                    const response = await getGroceryList(savedList.id, token);
                                                    const updatedList = response.list || response;
                                                    setSavedList(updatedList);
                                                    setGeneratedList(updatedList);
                                                    await loadDailyLists();
                                                } catch (err) {
                                                    console.error('Error reloading list:', err);
                                                    loadActiveList();
                                                }
                                            } else {
                                                loadActiveList();
                                            }
                                        }}
                                        onAddRecipe={async () => {
                                            // Reload the current list
                                            if (savedList?.id) {
                                                try {
                                                    const token = await user.getIdToken();
                                                    const response = await getGroceryList(savedList.id, token);
                                                    const updatedList = response.list || response;
                                                    setSavedList(updatedList);
                                                    setGeneratedList(updatedList);
                                                    await loadDailyLists();
                                                } catch (err) {
                                                    console.error('Error reloading list:', err);
                                                    loadActiveList();
                                                }
                                            } else {
                                                loadActiveList();
                                            }
                                        }}
                                        onDelete={async () => {
                                            setSavedList(null);
                                            setGeneratedList(null);
                                            setSelectedListId(null);
                                            await loadActiveList();
                                            await loadDailyLists();
                                        }}
                                    />
                                ) : (
                                    <div className="bg-neutral-0 p-4 hairline">
                                        <p className="font-sans text-base text-neutral-900">
                                            Select a daily list from the sidebar to view it.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </PremiumPlanningShell>
        );
    }

    if (showStockCheck) {
        if (generatingList && stockCheckData.length === 0) {
            return (
                <PremiumPlanningShell theme={planningTheme}>
                    <div className="px-8 py-6">
                        <div className="w-full">
                            <div className="bg-neutral-0 p-4 hairline">
                                <p className="font-sans text-base text-neutral-900 opacity-60">Generating grocery list...</p>
                            </div>
                        </div>
                    </div>
                </PremiumPlanningShell>
            );
        }
        
        return (
            <PremiumPlanningShell theme={planningTheme}>
                <div className="px-8 py-6">
                    <div className="w-full">
                        <StockCheckView
                            stockCheckData={stockCheckData}
                            onGenerateList={handleFinalizeList}
                            onCancel={() => {
                                setShowStockCheck(false);
                                setGeneratingList(false);
                            }}
                        />
                    </div>
                </div>
            </PremiumPlanningShell>
        );
    }

            return (
                <>
                    <OccasionNamePrompt
                        isOpen={showNamePrompt}
                        onClose={() => setShowNamePrompt(false)}
                        onContinue={handleNamePromptContinue}
                        title="Name Your Grocery List"
                    />
                    <PremiumPlanningShell theme={planningTheme}>
                        <div className="px-8 py-6">
                            <div className="w-full">
                                <div className="bg-neutral-0 p-4 hairline mb-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h1 className="font-sans text-3xl font-medium text-[#1b263b]">
                                            Generate Grocery List
                                        </h1>
                                        <span className="px-2 py-1 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider">PREMIUM</span>
                                    </div>
                                    
                                    {/* Tab Navigation */}
                                    <div className="flex gap-4 mb-6 border-b hairline">
                                        <button
                                            onClick={() => setActiveTab('lists')}
                                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                                activeTab === 'lists'
                                                    ? 'text-[#1b263b] border-b-2 border-[#1b263b]'
                                                    : 'text-neutral-900 opacity-60 hover:opacity-100'
                                            }`}
                                        >
                                            My Lists
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('quick')}
                                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                                activeTab === 'quick'
                                                    ? 'text-[#1b263b] border-b-2 border-[#1b263b]'
                                                    : 'text-neutral-900 opacity-60 hover:opacity-100'
                                            }`}
                                        >
                                            Quick Generate
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('occasions')}
                                            className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none ${
                                                activeTab === 'occasions'
                                                    ? 'text-[#1b263b] border-b-2 border-[#1b263b]'
                                                    : 'text-neutral-900 opacity-60 hover:opacity-100'
                                            }`}
                                        >
                                            From Occasions
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div className="mb-4 p-4 bg-red-50 hairline text-red-900">
                                        <p className="font-sans text-sm">{error}</p>
                                    </div>
                                )}

                                {activeTab === 'lists' && (
                                    <div>
                                        <h2 className="font-sans text-3xl font-medium text-[#1b263b] mb-4">
                                            Your Grocery Lists
                                        </h2>
                                        
                                        {loading && !generatingList ? (
                                            <p className="font-sans text-base text-neutral-900 opacity-60">Loading lists...</p>
                                        ) : savedLists.length === 0 ? (
                                            <p className="font-sans text-base text-neutral-900 opacity-60">
                                                No saved grocery lists yet. Create one using Quick Generate or From Occasions.
                                            </p>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {savedLists.map((list) => (
                                                    <div
                                                        key={list.id}
                                                        className="p-4 bg-neutral-50 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all cursor-pointer"
                                                        onClick={() => handleViewList(list.id)}
                                                    >
                                                        <h3 className="font-sans text-xl font-medium mb-2">
                                                            {list.name || 'Unnamed List'}
                                                        </h3>
                                                        {list.items && (
                                                            <p className="font-mono text-xs uppercase tracking-wider opacity-60">
                                                                {list.items.filter(item => !item.checked).length} / {list.items.length} items remaining
                                                            </p>
                                                        )}
                                                        {list.created_at && (
                                                            <p className="font-sans text-sm opacity-80 mt-2">
                                                                Created {new Date(list.created_at).toLocaleDateString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                            {activeTab === 'occasions' && (
                                <div>
                                    <h2 className="font-sans text-3xl font-medium text-[#1b263b] mb-4">
                                        Your Occasions
                                    </h2>
                                    
                                    {loading && !generatingList ? (
                                        <p className="font-sans text-base text-neutral-900 opacity-60">Loading occasions...</p>
                                    ) : generatingList ? (
                                        <p className="font-sans text-base text-neutral-900 opacity-60">Generating grocery list...</p>
                                    ) : occasions.length === 0 ? (
                                        <p className="font-sans text-base text-neutral-900 opacity-60">
                                            No occasions yet. Create one in Quick Generate.
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {occasions.map((occasion) => (
                                                <div
                                                    key={occasion.id}
                                                    className="p-4 bg-neutral-50 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all cursor-pointer"
                                                    onClick={() => handleGenerateFromOccasion(occasion.id)}
                                                >
                                                    <h3 className="font-sans text-xl font-medium mb-2">
                                                        {occasion.name}
                                                    </h3>
                                                    {occasion.description && (
                                                        <p className="font-sans text-sm opacity-80 mb-2">
                                                            {occasion.description}
                                                        </p>
                                                    )}
                                                    <p className="font-mono text-xs uppercase tracking-wider opacity-60">
                                                        {occasion.recipe_count || 0} recipes
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                        {activeTab === 'quick' && (
                            <div>
                                <h2 className="font-sans text-3xl font-medium text-[#1b263b] mb-4">
                                    Select Recipes
                                </h2>
                                
                                {loading && allRecipes.length === 0 ? (
                                    <p className="font-sans text-base text-neutral-900 opacity-60">Loading recipes...</p>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                                            {allRecipes.map((recipe) => {
                                                const isSelected = selectedRecipes.has(recipe.id);
                                                const servings = recipeServings[recipe.id] || 4;
                                                
                                                return (
                                                    <div
                                                        key={recipe.id}
                                                        className={`p-3 text-left text-xs bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all cursor-pointer ${
                                                            isSelected ? 'bg-neutral-900 text-neutral-0' : ''
                                                        }`}
                                                        onClick={() => handleRecipeSelection(recipe.id)}
                                                    >
                                                        <div className="font-sans font-medium mb-2">
                                                            {recipe.name || recipe.title}
                                                        </div>
                                                        {isSelected && (
                                                            <div className="mt-2">
                                                                <label className="font-mono text-xs uppercase tracking-wider block mb-1">
                                                                    Servings:
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={servings}
                                                                    onChange={(e) => handleServingsChange(recipe.id, parseInt(e.target.value) || 1)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="w-full px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-mono text-xs"
                                                                    style={{ color: '#0D0D0D' }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        
                                        {selectedRecipes.size > 0 && (
                                            <button
                                                onClick={handleGenerateList}
                                                disabled={generatingList}
                                                className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                                                style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                                onMouseEnter={(e) => e.target.style.opacity = '0.6'}
                                                onMouseLeave={(e) => e.target.style.opacity = '1'}
                                            >
                                                {generatingList ? 'Generating...' : 'Generate List'}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </PremiumPlanningShell>
                </>
            );
};

export default GenerateGroceryListPage;

